/* eslint-disable indent */
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CompletionStatus, FetchType } from '../../../enums';
import { Seniority } from '../../../enums/seniority';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import { CareerPath } from '../../../models/view/career-path';
import { LearnerCareerPath } from '../../../models/view/learner-career-path';
import { useAppActions, useAppState } from '../../../overmind';
import { ICareerPathFormValues } from './career-path.models';
import { CareerPathValidator, generateLPIText } from './career-path.helpers';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { AddTwoToneIcon, Anchor, Button, Chip, ChipWrapper, DropdownSelect, FieldController, FormGroup, IPillButton, PageHeader, PillButtonGroup, RequiredValidator, ThemeColours, capitalCase, colourString, useMatchScreenWidth } from '@keplerco/core';
import { getSubdomain } from '../../../library/helpers/get-subdomain';
import layoutStyles from '../../../design/layout.module.css';
import styles from './career-path.module.css';
import classNames from 'classnames';
import { useAnalysisBasePagePath } from '../use-analysis-base-page-path';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';

export function ChooseYourCareerPathsPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();
  const subdomain = getSubdomain();

  const actions = useAppActions();
  const { activeAssessmentConfig, companyVariables, user, forcedAssessments } = useAppState();

  const { control, trigger, setValue, reset } = useForm({ mode: 'all', reValidateMode: 'onChange' });

  const isMobile = useMatchScreenWidth('mobile');

  const buttonRef = useRef<HTMLButtonElement>(null);

  const [lastButtonRef, setLastButtonRef] = useState<HTMLButtonElement | null>(null);
  const [listCareerPaths, setListCareerPaths] = useState<CareerPath[]>();
  const [formValues, setFormValues] = useState<ICareerPathFormValues[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (lastButtonRef) {
      lastButtonRef.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastButtonRef]);

  useEffect(() => {
    // @Jedidja
    async function getData() {
      if (!user?.learnerSlug || !activeAssessmentConfig?.assessmentSlug) return;

      actions.startLoader({ path: PagePath.analysisChooseYourCareerPaths, type: FetchType.PageFetching });

      const skillAssessmentData = await actions.getCareerPaths();
      setListCareerPaths(skillAssessmentData);

      const learnerData = await actions.getLearnerCareerPaths({ companySlug: companyVariables.slug!, assessmentSlug: activeAssessmentConfig?.assessmentSlug, learnerSlug: user?.learnerSlug });

      const tempFormValues = learnerData?.map(learnerCareerPath => ({
        id: Date.now() + Math.random(),
        careerPath: skillAssessmentData?.find(temp => temp.careerPathSlug === learnerCareerPath.careerPathSlug),
        seniority: learnerCareerPath.seniorityLevelEnum,
      }));

      setFormValues(tempFormValues!);

      type FormValuesForResetType = {
        [key: string]: any;
      };

      const formValuesForReset: FormValuesForResetType = {};

      tempFormValues?.forEach(formValue => {
        formValuesForReset[`careerPath${formValue.id}`] = formValue.careerPath?.careerPathSlug;
        formValuesForReset[`seniority${formValue.id}`] = formValue.seniority;
      });

      reset(formValuesForReset);

      actions.stopLoader(PagePath.analysisChooseYourCareerPaths);
    }

    getData();
  }, []);

  function onClickAddHandler() {
    for (const formValue of formValues) {
      if (!formValue.careerPath) {
        setHasError(true);
        trigger();
        return;
      }

      if ((formValue.careerPath.includesEntrySkills || formValue.careerPath.includesMiddleSkills || formValue.careerPath.includesSeniorSkills) && formValue.seniority !== 0 && !formValue.seniority) {
        setHasError(true);
        trigger();
        return;
      }

      const nonUniqueCareerPaths = formValues.filter(temp => temp.careerPath === formValue.careerPath);
      if (nonUniqueCareerPaths.length > 1) {
        setHasError(true);
        trigger();
        return;
      }
    }

    setHasError(false);
    setFormValues(latest => [...latest, { id: Date.now() + Math.random(), careerPath: undefined, seniority: undefined }]);
    setLastButtonRef(buttonRef.current);
  }

  function onClickRemoveHandler(index: number) {
    setFormValues(latest => {
      const temp = [...latest];
      temp.splice(index, 1);
      return temp;
    });

    if (formValues.length <= 1) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }

  async function onSubmitHandler() {
    if (!formValues.length) {
      setHasError(true);
      return;
    }

    for (const formValue of formValues) {
      if (!formValue.careerPath) {
        setHasError(true);
        trigger();
        return;
      }

      if ((formValue.careerPath.includesEntrySkills || formValue.careerPath.includesMiddleSkills || formValue.careerPath.includesSeniorSkills) && formValue.seniority !== 0 && !formValue.seniority) {
        setHasError(true);
        trigger();
        return;
      }

      const nonUniqueCareerPaths = formValues.filter(temp => temp.careerPath?.careerPathName === formValue.careerPath?.careerPathName);
      if (nonUniqueCareerPaths.length > 1) {
        setHasError(true);
        trigger();
        return;
      }
    }

    setHasError(false);

    actions.startLoader({ path: PagePath.analysisChooseYourCareerPaths, type: FetchType.Sending });

    const learnerCareerPaths: LearnerCareerPath[] = [];

    for (const formValue of formValues) {
      learnerCareerPaths.push({ careerPathSlug: formValue.careerPath?.careerPathSlug, seniorityLevelEnum: formValue.seniority } as LearnerCareerPath);
    }

    await actions.saveLearnerCareerPaths(learnerCareerPaths);

    const response = await actions.getAssessmentConfig(activeAssessmentConfig!.assessmentSlug);

    if (response?.careerPathSelectionCompletionStatus === CompletionStatus.Completed) {
      keplerNavigate(basePagePath + PagePath.analysisRateYourSkills);
    } else {
      actions.clearAssessmentConfig();
    }

    actions.stopLoader(PagePath.analysisChooseYourCareerPaths);
  }

  function getFormLabel(index: number): string {
    return index === 0 ? 'primary' : index === 1 ? 'secondary' : 'tertiary';
  }

  function getChipColour(index: number): ThemeColours {
    return index === 0 ? 'primary' : index === 1 ? 'secondary' : 'accent-3';
  }

  function generateCareerPathButtons(index: number, seniority?: Seniority) {
    const buttons: IPillButton[] = [];

    if (formValues[index].careerPath?.includesEntrySkills) buttons.push({ id: Seniority.EntryLevel, label: 'Entry level', active: seniority === Seniority.EntryLevel });

    if (formValues[index].careerPath?.includesMiddleSkills) buttons.push({ id: Seniority.MidManagement, label: 'Mid-management', active: seniority === Seniority.MidManagement });

    if (formValues[index].careerPath?.includesSeniorSkills) buttons.push({ id: Seniority.SeniorManagement, label: 'Senior Management', active: seniority === Seniority.SeniorManagement });

    return buttons;
  }

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.analysisChooseYourCareerPaths}>
        <div className="wrapper" style={{ display: 'block', paddingBottom: isMobile ? 100 : 140 }}>
          <PageHeader
            breadcrumbs={
              forcedAssessments.length > 1
                ? [
                    {
                      name: 'Pending assessments',
                      url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
                    },
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: `Choose your ${generateLPIText(subdomain)}s`,
                      url: location.pathname,
                    },
                  ]
                : [
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: `Choose your ${generateLPIText(subdomain)}s`,
                      url: location.pathname,
                    },
                  ]
            }
            title={`Choose your ${generateLPIText(subdomain)}s`}
            subtitle={`Let us know which ${generateLPIText(subdomain)} you are currently working on and any other ${generateLPIText(subdomain)}s that you are considering.`}
          />

          {formValues.map((formValue, index) => {
            return (
              <div className="row" key={formValue.id}>
                <div className="column" style={{ maxWidth: '100%' }}>
                  <div className={classNames('card', styles.card)}>
                    {index > 0 && (
                      <div className={styles.cardRemoveArea}>
                        <button className={styles.removeButton} onClick={() => onClickRemoveHandler(index)}>
                          <small className={styles.removeButtonText}>REMOVE</small>
                        </button>
                      </div>
                    )}

                    <div className={styles.cardIconArea}>
                      <div className={styles.cardIcon}>
                        <object data={themedAssetUrl(`icons/path-${index + 1}.icon` + '.svg')} type="image/svg+xml">
                          <img src={themedAssetUrl(`icons/path-${index + 1}.icon` + '.svg')} alt="" />
                        </object>
                      </div>
                    </div>

                    <div className={styles.cardTextArea}>
                      <header style={{ position: 'relative' }}>
                        <ChipWrapper className={styles.cardChipWrapper}>
                          <Chip themeColour={getChipColour(index)}>{capitalCase(`${getFormLabel(index)} ${generateLPIText(subdomain)}`)}</Chip>
                        </ChipWrapper>
                      </header>

                      <div className={styles.cardBody}>
                        <div
                          ref={ref => {
                            if (ref) {
                              setLastButtonRef(ref as unknown as HTMLButtonElement);
                            }
                          }}
                        >
                          <FormGroup label={`What is your ${getFormLabel(index)} ${generateLPIText(subdomain)}?`}>
                            <FieldController
                              name={`careerPath${formValue.id}`}
                              control={control}
                              rules={new CareerPathValidator(`Select a ${generateLPIText(subdomain)}`, formValues)}
                              render={({ field, fieldState }) => {
                                return (
                                  <DropdownSelect
                                    {...field}
                                    label="Career path"
                                    options={
                                      listCareerPaths
                                        ?.filter(listCareerPath => {
                                          if (formValue.careerPath?.careerPathSlug === listCareerPath.careerPathSlug) {
                                            return true;
                                          }
                                          return !formValues.some(fv => fv.careerPath?.careerPathSlug === listCareerPath.careerPathSlug);
                                        })
                                        .map(listCareerPath => ({
                                          value: listCareerPath.careerPathSlug,
                                          label: listCareerPath.careerPathName,
                                          onClick: () => {
                                            setFormValues(currentState => {
                                              return currentState.map(fv => {
                                                if (fv.id === formValue.id) {
                                                  return { ...fv, careerPath: listCareerPath };
                                                }
                                                return fv;
                                              });
                                            });

                                            setValue(`careerPath${formValue.id}`, listCareerPath.careerPathSlug);

                                            setHasError(false);
                                          },
                                        })) ?? []
                                    }
                                    defaultValue={formValue.careerPath?.careerPathName}
                                    validation={{
                                      dirty: !!fieldState.isDirty || !!fieldState.error,
                                      invalid: !!fieldState.invalid || !!fieldState.error,
                                      message: fieldState.error?.message ?? `Select a ${generateLPIText(subdomain)}`,
                                    }}
                                    large
                                    responsive
                                  />
                                );
                              }}
                            />
                          </FormGroup>
                        </div>

                        {(formValue.careerPath?.includesEntrySkills || formValue.careerPath?.includesMiddleSkills || formValue.careerPath?.includesSeniorSkills) && (
                          <FormGroup label="What is your current seniority level within this path?">
                            <FieldController
                              name={`seniority${formValue.id}`}
                              control={control}
                              rules={new RequiredValidator('Select your seniority')}
                              render={() => {
                                return (
                                  <PillButtonGroup
                                    backgroundColour="borders_2"
                                    activeTextColour="text"
                                    buttons={generateCareerPathButtons(index, formValue.seniority)}
                                    layout="fit"
                                    onClick={(buttons: IPillButton[]) => {
                                      if (typeof buttons[0].id === undefined) return;

                                      setFormValues(currentState => {
                                        const newState = structuredClone(currentState);
                                        newState[index].seniority = buttons[0].id;
                                        return newState;
                                      });

                                      setValue(`seniority${formValue.id}`, buttons[0].id);
                                      trigger(`seniority${formValue.id}`);
                                    }}
                                  />
                                );
                              }}
                            />
                          </FormGroup>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {formValues.length < 3 && (
            <div className="row">
              <div className="column">
                <button className={styles.addButton} type="button" onClick={onClickAddHandler} ref={buttonRef}>
                  <AddTwoToneIcon primaryTone="default" secondaryTone="default" />
                  <span className="h4" style={{ color: colourString('default') }}>
                    &nbsp;ADD {!!formValues.length ? 'ANOTHER' : 'A'} {generateLPIText(subdomain).toUpperCase()}
                  </span>
                </button>
              </div>
            </div>
          )}

          <div className={classNames('formErrorMessage', { invisible: !hasError })} style={{ marginTop: 24 }}>
            {formValues.length === 0 ? 'Please add at least one career path' : 'Please complete all career path information before proceeding'}
          </div>
        </div>
      </PageLoaderLayer>

      <footer className={layoutStyles.footer}>
        <div className={layoutStyles.footerContent}>
          <Anchor arrow reverse block onClick={() => keplerNavigate(basePagePath)}>
            Back
          </Anchor>

          <Button type="button" onClick={onSubmitHandler}>
            Submit
          </Button>
        </div>
      </footer>
    </React.Fragment>
  );
}
